import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';

export interface Attractions {
  id: number;
  name: string;
}

export interface Transports {
  id: number;
  name: string;
}

export interface Marker {
  description: string;
  index: number;
  latitude: string;
  long_description: string;
  longitude: string;
  time_to_arrive: string;
  link: string;
  name: string;
  price: string;
  hidden_gem?: boolean;
  estimated_time_to_next?: string;
}

export interface GuideRequest {
  country: string;
  city: string;
  duration: number;
  day_period: string;
  payment_preference: string;
  physical_activity: string;
  public: boolean;
  attractions: number[];
  transports: number[];
}

export interface Guide {
  id: number;
  title?: string;
  city: string;
  country: string;
  image_url: string;
  day_period?: string;
  duration?: number;
  attractions?: Attractions[];
  transports?: Transports[];
  markers?: Marker[];
  payment_preference?: string;
  physical_activity?: string;
  public?: boolean;
  user_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaginationMeta {
  current_page: number;
  from: number;
  per_page: number;
  to: number;
  total: number;
  total_pages: number;
}

interface GuidesState {
  items: Guide[];
  lastGuides: Guide[];
  savedGuides: Guide[];
  currentGuide: Guide | null;
  loading: boolean;
  error: string | null;
  pagination: PaginationMeta | null;
  filters: {
    country?: string;
    city?: string;
    duration?: number;
    searchTerm?: string;
  };
}

const initialState: GuidesState = {
  items: [],
  lastGuides: [],
  savedGuides: [],
  currentGuide: null,
  loading: false,
  error: null,
  pagination: null,
  filters: {},
};

export const guidesSlice = createSlice({
  name: 'guides',
  initialState,
  reducers: {
    fetchGuidesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchGuidesSuccess: (state, action: PayloadAction<{data: Guide[], meta: PaginationMeta}>) => {
      state.items = action.payload.data;
      state.pagination = action.payload.meta;
      state.loading = false;
    },
    fetchGuidesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUserGuidesSuccess: (state, action: PayloadAction<{last_guides: Guide[], saved_guides: Guide[]}>) => {
      state.lastGuides = action.payload.last_guides;
      state.savedGuides = action.payload.saved_guides;
    },
    setCurrentGuide: (state, action: PayloadAction<Guide | null>) => {
      state.currentGuide = action.payload;
    },
    saveGuide: (state, action: PayloadAction<Guide>) => {
      console.log(state.savedGuides, action.payload)
      if (!state.savedGuides?.includes(action.payload)) {
        state.savedGuides.push(action.payload);
      }
    },
    unsaveGuide: (state, action: PayloadAction<Guide>) => {
      state.savedGuides = state.savedGuides.filter(id => +id !== +action.payload.id);
    },
    setFilters: (state, action: PayloadAction<Partial<GuidesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    addGuide: (state, action: PayloadAction<Guide>) => {
      state.items.unshift(action.payload);
      state.lastGuides.unshift(action.payload);
    },
  },
});

export const {
  fetchGuidesStart,
  fetchGuidesSuccess,
  fetchGuidesFailure,
  fetchUserGuidesSuccess,
  setCurrentGuide,
  saveGuide,
  unsaveGuide,
  setFilters,
  clearFilters,
  addGuide,
} = guidesSlice.actions;

export const selectSavedGuides = (state) => state.guides.savedGuides;

export const selectSavedGuideIds = createSelector(
    [selectSavedGuides],
    guides => guides?.map(g => g.id)
);

export default guidesSlice.reducer;
